import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../../assets/ChatSelectionsPage.css";
import "../../../src/App.css";
import AlertBox from "../AlertBox/AlertBox";
import { useTranslation } from "react-i18next";
import SiteHeader from "../SiteHeader";
import Modal from "../Modal";
import NetworkBanner from "../NetworkBanner";

/** Constants */
const BASE_URL = process.env.REACT_APP_BASE_URL;

// TODO:: check this val, should either be slightly more or less than server timeout
const TIMEOUT = (Number(process.env.REACT_APP_LONG_RESPONSE_TIMEOUT) * 1.1) || 900000 * 1.1;

const DEFAULT_ERROR_KEY = "chats.defaultError";

const PollingMode = {
    IDLE: "idle",
    LOADING_GROUPS: "loading_groups",
    PROCESSING_MESSAGES: "processing_messages",
    AWAITING_RESCAN: "awaiting_rescan",

    getUserLabel(mode) {
        const labels = {
            [this.IDLE]: "",
            [this.LOADING_GROUPS]: "chats.loadingGroups",
            [this.PROCESSING_MESSAGES]: "chats.donatingLinks",
            [this.AWAITING_RESCAN]: "chats.rescanNeeded",
        }
        return labels[mode] || mode
    }
}

// Polling interval for /api/repair-qr-code once a mid-donation rescan is
// detected. Same cadence as the existing extraction-progress poll below.
const RESCAN_QR_POLL_INTERVAL_MS = 2000;

const ChatSelectionsPage = () => {
    // groups load at page mount
    const { t } = useTranslation();
    // Transient-failure tolerance for the polling loops (2026-10-06): a browser
    // network blip must not abort a donation whose phone-side link is fine.
    const MAX_TRANSIENT_POLL_FAILURES = 150; // ~5 minutes at 2s
    const pollFailuresRef = useRef(0);
    const [pollFailures, setPollFailures] = useState(0);
    const [mode, setMode] = useState(PollingMode.LOADING_GROUPS);
    const [progress, setProgress] = useState(0);
    const [groups, setGroups] = useState([]);
    // 2026-10-21: no select/deselect — every eligible group (5+ members) is
    // donated. A returning donor's groups already donated earlier are marked;
    // the new donation covers the not-yet-donated ones (all of them if none).
    const [isReturning, setIsReturning] = useState(false);
    const [declineOpen, setDeclineOpen] = useState(false);
    const [declining, setDeclining] = useState(false);
    const newGroups = groups.filter((g) => !g.already_donated);
    const donatedBefore = groups.length - newGroups.length;
    const groupsToDonate = newGroups.length > 0 ? newGroups : groups;
    const selectedGroupIds = groupsToDonate.map((g) => g.id._serialized);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [alert, setAlert] = useState(null);
    // Set when the backend reports awaiting_rescan=true mid-donation (see
    // /api/extraction-progress in server/routes/dataRoutes.js) - some
    // selected groups didn't get their full message history on the first
    // pass, and WhatsApp needs a fresh device-link scan to retry them. Holds
    // the QR image once /api/repair-qr-code has one ready.
    const [rescanQrUrl, setRescanQrUrl] = useState("");
    const navigate = useNavigate();

    // state transitions. Note: start state is PollingMode.LOADING_GROUPS

    const resetToIdle = () => {
        setMode(PollingMode.IDLE);
        setProgress(0);
        setIsButtonDisabled(false);
        setRescanQrUrl("");
    };

    const startProcessingMessages = () => {
        setMode(PollingMode.PROCESSING_MESSAGES);
        setProgress(0);
        setIsButtonDisabled(true);
        setRescanQrUrl("");
    };

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    const getProgressMessage = () => {
        switch(mode) {
        case PollingMode.LOADING_GROUPS:
            return t("chats.progressLoading");
        case PollingMode.PROCESSING_MESSAGES:
            return t("chats.progressProcessing");
        case PollingMode.AWAITING_RESCAN:
            return t("chats.progressRescan");
        default:
            return "";
        }
    }

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const res = await axios.post(new URL("/api/job", BASE_URL),{
                    type: "fetch-group-chats"
                });

                if (res.status !== 202) {
                    throw ("Request to kick off job not accepted");
                }
            } catch (error) {
                console.error("Error fetching groups:", error);
                setAlert({ level: "warning", message: parseError(error, t(DEFAULT_ERROR_KEY))});
                resetToIdle();
            }
        }
        
        fetchGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // run on mount - no dependencies

    // TODO:: these two modes use different polling logic - combine when backend supports
    useEffect(() => {
        let pollingInterval;
        let intervalMs = 2000;

        if (mode === PollingMode.LOADING_GROUPS) {
            pollingInterval = setInterval(async () => {
                try {
                    const res = await axios.get(new URL("/api/job/status", BASE_URL));
                    const { progress, status } = res.data;
                    setProgress(progress);

                    if (status === "COMPLETED") {
                        clearInterval(pollingInterval); // Stop immediately
                        await getGroupChats();
                        await sleep(intervalMs); // pause a beat to allow progress bar to show at 100
                        resetToIdle(); 
                    } else if (status === "FAILED") {
                        throw new Error(res.data?.error || "Failed to fetch groups from WhatsApp")
                    }
                } catch (error) {
                    if (!error.response && pollFailuresRef.current < MAX_TRANSIENT_POLL_FAILURES) {
                        pollFailuresRef.current += 1; setPollFailures(pollFailuresRef.current);
                        return; // transient network blip: keep polling (2026-10-06)
                    }
                    clearInterval(pollingInterval);
                    setAlert({ level: "warning", message: parseError(error, t(DEFAULT_ERROR_KEY))});
                    resetToIdle();
                }
            }, intervalMs);
        }

        return () => clearInterval(pollingInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable; getGroupChats is a component-scoped function (adding it would re-run on every render)
    }, [mode]);


    // Polling for extraction progress to show actual progress
    useEffect(() => {
        let pollingInterval;

        if (mode === PollingMode.PROCESSING_MESSAGES) {
            pollingInterval = setInterval(async () => {
                try {
                    const res = await axios.get(new URL("/api/extraction-progress", BASE_URL), { timeout: TIMEOUT });
                    const { progress, awaiting_rescan } = res.data;

                    // A subset of the donor's selected groups didn't get a full
                    // history sync on the first pass, so the backend is forcing a
                    // fresh device-link pairing to retry them (see
                    // force_repair_for_incomplete_history in
                    // server/whatsApp/baileys/client.js). Pause the normal
                    // progress display and switch to the rescan flow instead of
                    // leaving the participant staring at a stalled progress bar.
                    if (awaiting_rescan) {
                        clearInterval(pollingInterval);
                        setMode(PollingMode.AWAITING_RESCAN);
                        return;
                    }

                    // Update progress bar state
                    setProgress(progress);

                    if (pollFailuresRef.current) { pollFailuresRef.current = 0; setPollFailures(0); }

                    // Stop polling when the process reaches 100%
                    if (progress === 100) {
                        clearInterval(pollingInterval);
                        const incompleteGroupCount = await saveAllData();
                        navigate("/dashboard", { state: { selection: "yes", incompleteGroupCount } });
                    }
                } catch (error) {
                    if (!error.response && pollFailuresRef.current < MAX_TRANSIENT_POLL_FAILURES) {
                        pollFailuresRef.current += 1; setPollFailures(pollFailuresRef.current);
                        return; // transient network blip: keep polling (2026-10-06)
                    }
                    clearInterval(pollingInterval);
                    console.error("Error polling progress:", error);
                    setAlert({ level: "warning", message: parseError(error, t(DEFAULT_ERROR_KEY))});
                    setMode(PollingMode.IDLE);
                }
            }, 2000); // Poll every 2 seconds
        }

        return () => {
            // Cleanup interval when the component is unmounted or polling stops
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable; getGroupChats is a component-scoped function (adding it would re-run on every render)
    }, [mode, navigate]);

    // While awaiting a mid-donation rescan: fetch the new QR code (retrying
    // until /api/repair-qr-code has one ready), then watch
    // /api/extraction-progress until awaiting_rescan clears - that means the
    // participant scanned successfully and the backend has resumed
    // extraction - at which point we hand control back to the normal
    // PROCESSING_MESSAGES polling effect above.
    useEffect(() => {
        let pollingInterval;

        if (mode === PollingMode.AWAITING_RESCAN) {
            pollingInterval = setInterval(async () => {
                try {
                    if (!rescanQrUrl) {
                        const qrRes = await axios.get(new URL("/api/repair-qr-code", BASE_URL));
                        if (qrRes.data?.qrCodeImageUrl) {
                            setRescanQrUrl(qrRes.data.qrCodeImageUrl);
                        }
                        return; // check extraction-progress on the next tick once we have a QR
                    }

                    const progressRes = await axios.get(new URL("/api/extraction-progress", BASE_URL));
                    if (!progressRes.data.awaiting_rescan) {
                        clearInterval(pollingInterval);
                        setRescanQrUrl("");
                        setMode(PollingMode.PROCESSING_MESSAGES);
                    }
                } catch (error) {
                    if (!error.response && pollFailuresRef.current < MAX_TRANSIENT_POLL_FAILURES) {
                        pollFailuresRef.current += 1; setPollFailures(pollFailuresRef.current);
                        return; // transient network blip: keep polling (2026-10-06)
                    }
                    clearInterval(pollingInterval);
                    console.error("Error during rescan flow:", error);
                    setAlert({ level: "warning", message: parseError(error, t(DEFAULT_ERROR_KEY))});
                    setMode(PollingMode.IDLE);
                }
            }, RESCAN_QR_POLL_INTERVAL_MS);
        }

        return () => clearInterval(pollingInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable; getGroupChats is a component-scoped function (adding it would re-run on every render)
    }, [mode, rescanQrUrl]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        startProcessingMessages();

        try {
            // Step 1: Start the extraction process
            const startExtractionResponse = await axios.post(
                new URL("/api/start-extraction", BASE_URL),
                { group_ids: selectedGroupIds }
            );

            if (startExtractionResponse.status !== 200) {
                throw new Error("Unable to start extracting messages");
            }

        } catch (error) {
            console.error("Error processing groups:", error);
            setAlert({ level: "warning", message: parseError(error, t("chats.startError"))});
            resetToIdle();
        }
    };

    const getGroupChats = async () => {
        const res = await axios.get(new URL("/api/group-chats", BASE_URL), { timeout: TIMEOUT });
        const group_chats = res.data.group_chats;
        if (Array.isArray(group_chats) && group_chats.length > 0) {
            setGroups(group_chats);
            setIsReturning(Boolean(res.data.returning));
        } else {
            setAlert({ level: "warning", message: t("chats.noGroups") });
        }
    };

    // "No" = exit the study: confirm first, then tell the server (it disconnects
    // the WhatsApp link and tears the session down) and show the exit page.
    const declineSentRef = useRef(false);
    const handleDecline = async () => {
        if (declineSentRef.current) return; // one request per session, however the button is clicked
        declineSentRef.current = true;
        setDeclining(true);
        try {
            await axios.post(new URL("/api/decline", BASE_URL), {}, { timeout: 15000 });
        } catch (error) {
            console.warn("decline request failed:", error && error.message);
        }
        navigate("/thank-you", { replace: true, state: { exit: true } });
    };

    // Step 3: Handle processing the groups after extraction is complete
    const saveAllData = async () => {
        // Call the backend to process the groups after extraction completes.
        // One automatic retry (2026-10-04): the backend's writes are
        // idempotent, so a transient failure must not turn into an error
        // screen for the participant.
        const post = () => axios.post(new URL("/api/save-all-data", BASE_URL));
        let response;
        try {
            response = await post();
        } catch (firstError) {
            await new Promise((r) => setTimeout(r, 3000));
            response = await post();
        }
        if (response.status !== 200) {
            throw new Error("Failed to submit data");
        }
        // incomplete_group_count (see server/routes/dataRoutes.js): how many
        // selected groups couldn't be fully synced even after the automatic
        // rescan retry. Passed through to the dashboard so it can mention
        // this gently rather than presenting the donation as fully silent
        // about it - it's still a successful donation overall.
        return response.data?.incomplete_group_count || 0;
    };

    // TODO:: standardize backend error format and delete this stopgap
    const parseError = (error, defaultMessage="Something went wrong") => {
        if (error.response?.data) {
            const data = error.response.data;
            
            // handle all our backend error payload differences
            return data.error || data.msg || data.message || defaultMessage;
        }
        
        // axios / network errors
        return error.message || defaultMessage;
    };

    return (
        <div className="app-container">
            <SiteHeader />

            <div className="ChatSelectionsPage">
                <NetworkBanner retrying={pollFailures > 0} />

                {(mode === PollingMode.IDLE) && (groups?.length > 0) && (
                    <>
                        <div className="donate-section">
                            <p>{t("chats.allGroupsIntro")}</p>
                            {isReturning && donatedBefore > 0 && newGroups.length > 0 && (
                                <p><b>{t("chats.returningIntro", { newCount: newGroups.length, doneCount: donatedBefore })}</b></p>
                            )}
                            {isReturning && donatedBefore > 0 && newGroups.length === 0 && (
                                <p><b>{t("chats.returningNothingNew", { count: groups.length })}</b></p>
                            )}
                            <p><b>{t("chats.groupCount", { count: groupsToDonate.length })}</b></p>
                        </div>
                        <div className="form-container">
                            <ul className="group-list">
                                {groups.map((group) => (
                                    <li key={group.id._serialized} style={{ opacity: group.already_donated && newGroups.length > 0 ? 0.6 : 1 }}>
                                        <span>{group.name || group.subject}</span>
                                        {group.already_donated && (
                                            <span className="forwarded-badge">{t("chats.alreadyDonatedBadge")}</span>
                                        )}
                                        {group.has_forwarded_many_times && (
                                            <span className="forwarded-badge">{t("chats.forwardedBadge")}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="donate-section">
                            <p><b>{t("chats.donateQuestion")}</b></p>
                            <p style={{ fontSize: "13px", color: "#555", marginTop: "-8px" }}>
                                {t("chats.donateNote")}
                            </p>
                            <button
                                className="yes-button"
                                onClick={handleSubmit}
                                disabled={isButtonDisabled || selectedGroupIds.length === 0}
                            >
                                {t("chats.yes")}
                            </button>
                            <button className="no-button" type="button" onClick={() => setDeclineOpen(true)} disabled={isButtonDisabled}>
                                {t("chats.no")}
                            </button>
                        </div>
                        <Modal isOpen={declineOpen} onClose={() => !declining && setDeclineOpen(false)}>
                            <h3 style={{ marginTop: 0 }}>{t("chats.declineTitle")}</h3>
                            <p>{t("chats.declineBody")}</p>
                            {declining ? (
                                <p><i>{t("chats.declining")}</i></p>
                            ) : (
                                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                                    <button className="no-button" type="button" onClick={handleDecline}>{t("chats.declineConfirm")}</button>
                                    <button className="yes-button" type="button" onClick={() => setDeclineOpen(false)}>{t("chats.declineCancel")}</button>
                                </div>
                            )}
                        </Modal>
                    </>
                )}

                {(mode === PollingMode.AWAITING_RESCAN) && (
                    <div className="loading-container rescan-container">
                        <h2>{t(PollingMode.getUserLabel(mode))}</h2>
                        <p>
                            {t("chats.rescanIntro")}
                        </p>
                        <p style={{ fontSize: "13px", color: "#555", marginTop: "4px" }}>
                            {getProgressMessage()}
                        </p>
                        {rescanQrUrl ? (
                            <img
                                src={rescanQrUrl}
                                alt={t("chats.rescanAlt")}
                                className="rescan-qr-image"
                            />
                        ) : (
                            <div className="spinner-container">
                                <div className="loading-spinner"></div>
                                <p>{t("chats.preparingQr")}</p>
                            </div>
                        )}
                        <p style={{ fontSize: "13px", color: "#555", marginTop: "12px" }}>
                            {t("chats.rescanAuto")}
                        </p>
                    </div>
                )}

                {(mode !== PollingMode.IDLE && mode !== PollingMode.AWAITING_RESCAN) && (
                    <div className="loading-container">
                        <h2>{t(PollingMode.getUserLabel(mode))}</h2>
                        <p>{getProgressMessage()}</p>
                        <p style={{ fontSize: "13px", color: "#555", marginTop: "4px" }}>
                            {t("chats.keepOpen")}
                        </p>
                        <div className="progress-bar-container">
                            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                        </div>
                        <p>{progress}%</p>
                    </div>
                )}

                {alert && (
                    <AlertBox
                        header={t("chats.errorHeader")}
                        message={alert.message}
                        level={alert.level}
                        onClose={() => setAlert(null)}
                    />
                )}
            </div>

            <footer className="footer-chat-selections">
                <div className="footer-logo">GU</div>
                <p className="footer-text">
                    {t("common.footer", { year: new Date().getFullYear() })}
                </p>
            </footer>
        </div>
    );
}
export default ChatSelectionsPage;